import { Box, Typography, TextField, InputAdornment } from '@mui/material';
import { useState, useMemo } from 'react';
import SearchIcon from '@mui/icons-material/Search';
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined';

import { colors } from '@src/styles';
import { fadeInUpSX, staggerChildrenSX } from '@src/styles/animations';
import { CanonicalItemDTO } from '@src/services/automations/modules/v1/canonicals/dto/canonicals.get.response.dto';
import { CanonicalCard } from './CanonicalCard';
import { CanonicalCardSkeleton } from './CanonicalCardSkeleton';
import { monoLabelSX, searchFieldSX, titleFontFamily } from './canonicalStyles';

const SKELETON_COUNT = 8;

export interface CanonicalGridProps {
  isLoadingCanonicals: boolean;
  canonicals: CanonicalItemDTO[];
  onCanonicalClick: (canonical: CanonicalItemDTO) => void;
  onCreateNew: () => void;
}

export const CanonicalGrid = ({ isLoadingCanonicals, canonicals, onCanonicalClick }: CanonicalGridProps) => {
  const [searchTerm, setSearchTerm] = useState('');

  const visibleCanonicals = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    const filtered = term
      ? canonicals.filter(
          (canonical) =>
            canonical.key.toLowerCase().includes(term) || canonical.description?.toLowerCase().includes(term),
        )
      : canonicals;
    return [...filtered].sort((a, b) => a.key.localeCompare(b.key, 'pt-BR'));
  }, [canonicals, searchTerm]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.75, height: '100%', minHeight: 0 }}>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'flex-end',
          gap: 2,
          flexShrink: 0,
          ...fadeInUpSX(),
        }}
      >
        <Box sx={{ flex: '999 1 320px', minWidth: 0 }}>
          <Typography
            component="h1"
            sx={{
              fontFamily: titleFontFamily,
              fontSize: '24px',
              fontWeight: 500,
              letterSpacing: '-0.03em',
              lineHeight: 1.1,
              color: colors.primary.white,
              marginBottom: '2px',
            }}
          >
            Canonicals
          </Typography>
          <Typography sx={{ fontSize: '12.5px', color: colors.brand.textSecondary }}>
            Recursos canônicos mantidos por Solutions, seus schemas e as organizações que os consomem.
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: '1 1 320px', minWidth: 0 }}>
          <TextField
            placeholder="Buscar por chave ou descrição…"
            size="small"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            inputProps={{ 'aria-label': 'Buscar recurso canônico' }}
            sx={{ ...searchFieldSX, flex: 1, minWidth: 0 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: colors.brand.textMuted, fontSize: 18 }} />
                </InputAdornment>
              ),
            }}
          />
          <Typography sx={{ ...monoLabelSX, whiteSpace: 'nowrap' }}>
            {isLoadingCanonicals ? '—' : visibleCanonicals.length} recursos
          </Typography>
        </Box>
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', paddingBottom: 1 }}>
        {!isLoadingCanonicals && visibleCanonicals.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 1,
              paddingTop: 10,
              textAlign: 'center',
              ...fadeInUpSX(),
            }}
          >
            <InboxOutlinedIcon sx={{ fontSize: 40, color: colors.brand.textMuted }} />
            <Typography sx={{ fontSize: 15, fontWeight: 600, color: colors.primary.white }}>
              {canonicals.length === 0 ? 'Nenhum recurso canônico' : 'Nada encontrado'}
            </Typography>
            <Typography sx={{ fontSize: 13, color: colors.brand.textSecondary, maxWidth: 380 }}>
              {canonicals.length === 0
                ? 'Ainda não há recursos canônicos cadastrados.'
                : `Nenhum recurso corresponde a "${searchTerm.trim()}". Tente outra chave ou parte da descrição.`}
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 0.75,
              ...(!isLoadingCanonicals && staggerChildrenSX('button', 16, 20)),
            }}
          >
            {isLoadingCanonicals
              ? Array.from({ length: SKELETON_COUNT }, (_, index) => <CanonicalCardSkeleton key={index} />)
              : visibleCanonicals.map((canonical) => (
                  <CanonicalCard key={canonical.id} canonical={canonical} onCanonicalClick={onCanonicalClick} />
                ))}
          </Box>
        )}
      </Box>
    </Box>
  );
};
